import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import { get } from 'lodash';


import { TIMESGRID_MOBILE_CONFIG } from '../constants/ViewConstants';
import getExistingPatientUrl from '../utils/getExistingPatientUrl';
import CreateTimesGridContent from './CreateTimesGridContent';
import GovernmentInsuranceNotice from './GovernmentInsuranceNotice';

import * as styled from './content-primitives';

class MobileTimesGridContent extends PureComponent {
    static propTypes = {
        loading: PropTypes.bool,
        onClickPhoneNumber: PropTypes.func,
        phoneNumber: PropTypes.string,
        profileUrl: PropTypes.string,
        showExistingPatientLink: PropTypes.bool,
        linkTarget: PropTypes.string,
        times: PropTypes.array,
        theme: PropTypes.shape({
            components: PropTypes.shape({
                AppointmentPicker: PropTypes.shape({
                    config: PropTypes.shape({
                        showExistingPatientsLink: PropTypes.bool,
                        showPhoneNumberIfNoAvailability: PropTypes.bool,
                    }),
                }),
            }),
        }),
    };

    static defaultProps = {
        linkTarget: '_top',
    }

    componentDidUpdate(prevProps) {
        if (prevProps.times !== this.props.times && this.$scroll) {
            this.$scroll.scrollLeft = 0;
        }
    }

    render() {
        return (
            <styled.MobileWrapper data-test="ap-mobile-content">
                {this.maybeRenderSlots()}
                {this.maybeRenderFallbackContent()}
            </styled.MobileWrapper>
        );
    }

    maybeRenderSlots() {
        if (!this.props.showEntireTimesGridMessage || this.props.loading) {
            return (
                <styled.MobileScroller
                    data-test="ap-mobile-content-times"
                    innerRef={node => { this.$scroll = node; }}
                >
                    {this.props.loading ? this.renderLoadingIndicator() : this.renderSlots()}
                </styled.MobileScroller>
            );
        }
    }

    renderLoadingIndicator() {
        return (
            <styled.MobileLoadingSlot>
                &nbsp;
            </styled.MobileLoadingSlot>
        );
    }

    renderSlots() {
        return this.props.times.map((gridRow, rowIndex) => (
            <styled.MobileSlotWrapper
                key={rowIndex}
            >
                {gridRow.times.map((time, colIndex) => this.renderSlot(time, colIndex, rowIndex))}
            </styled.MobileSlotWrapper>
        ));
    }

    renderSlot(time, colIndex) {
        const isEmpty = !time.datetime;

        let SlotElement;

        if (time.isMoreBtn) {
            SlotElement = styled.MobileMoreButton;
        } else if (isEmpty) {
            SlotElement = styled.MobileEmptySlot;
        } else {
            SlotElement = styled.MobileSlot;
        }

        return this.renderSlotVariant(SlotElement, time, colIndex);
    }

    renderSlotVariant(SlotElement, time, colIndex) {
        const key = time.id || colIndex;
        const slotID = time.id;
        const slotDateTime = time.datetime;

        switch (SlotElement) {
            case styled.MobileSlot: {
                return (
                    <SlotElement
                        key={key}
                        data-id={slotID}
                        data-prod-guids="timeslot-mobile:[4c2b7f86,cfa074a0,d1d2f34b,46ac31b8]"
                        data-test={`ap-mobile-content-timeslot-${slotID}`}
                        href={time.url}
                        onClick={this.handleTimeClicked}
                        rel="nofollow"
                        target={this.props.linkTarget}
                    >
                        {slotDateTime}
                    </SlotElement>
                );
            }

            case styled.MobileEmptySlot: {
                return (
                    <SlotElement
                        key={key}
                        data-id={slotID}
                        data-test="ap-mobile-content-timeslot-empty"
                    >
                        &nbsp;
                    </SlotElement>
                );
            }

            case styled.MobileMoreButton: {
                return (
                    <SlotElement
                        key={key}
                        data-has-more-button
                        data-id={slotID}
                        data-test="ap-mobile-content-more-times"
                        onClick={this.handleTimeClicked}
                    >
                        more
                    </SlotElement>
                );
            }
        }
    }

    maybeRenderFallbackContent() {
        if (this.props.showEntireTimesGridMessage && !this.props.loading) {
            return (
                <styled.MobileNoTimesView>
                    {this.renderFallbackContent()}
                    {this.maybeRenderExistingPatientLink()}
                </styled.MobileNoTimesView>
            );
        }
    }

    renderFallbackContent() {
        const { defaultMessages } = this.props.theme.components.AppointmentPicker;

        if (this.props.showGovernmentInsuranceNotice) {
            return (
                <styled.MobileNonBookableMessage
                    key="government-insurance-notice"
                    data-test="ap-mobile-content-government-insurance-notice"
                >
                    <GovernmentInsuranceNotice />
                </styled.MobileNonBookableMessage>
            );
        } else if (this.props.showNextTimeAvailable) {
            return (
                <styled.MobileNextAvailabilityLink
                    key="next-availability"
                    data-prod-guids="next-availability-mobile:[4c2b7f86,cfa074a0,d1d2f34b,46ac31b8]"
                    data-test="ap-mobile-content-next-appointment-link"
                    onClick={this.handleNextAvailClick}
                >
                    <FormattedMessage
                        defaultMessage={defaultMessages.NextAvailability}
                        id="AppointmentPicker.MobileView.Content.NextAvailability"
                        values={{ nextAvailableDate: this.props.nextAvailDate }}
                    />
                </styled.MobileNextAvailabilityLink>
            );
        } else if (get(this.props.theme, 'components.AppointmentPicker.config.showPhoneNumberIfNoAvailability')
            && this.props.phoneNumber) {
            return (
                <styled.MobilePhoneNumberLink
                    key="phone-number-link"
                    data-test="ap-mobile-content-phone-number-link"
                    href={`tel:${this.props.phoneNumber.replace(/\D/g, '')}`}
                    onClick={this.props.onClickPhoneNumber}
                >
                    <FormattedMessage
                        defaultMessage={defaultMessages.NoAvailabilityWithCta}
                        id="AppointmentPicker.MobileView.Content.NoAvailabilityWithCta"
                        values={{ phoneNumber: this.props.phoneNumber }}
                    />
                </styled.MobilePhoneNumberLink>
            );
        }

        return (
            <styled.MobileNonBookableMessage
                key="no-availability"
                data-prod-guids="no-availability-message-mobile:[4c2b7f86]"
                data-test="ap-mobile-content-no-appointments-div"
            >
                <FormattedMessage
                    defaultMessage={defaultMessages.NoAvailability}
                    id="AppointmentPicker.MobileView.Content.NoAvailability"
                />
            </styled.MobileNonBookableMessage>
        );
    }

    maybeRenderExistingPatientLink() {
        const { defaultMessages, config } = this.props.theme.components.AppointmentPicker;

        if (this.props.profileUrl && this.props.showExistingPatientLink && config.showExistingPatientsLink) {
            const urlWithExistingPatientFlag = getExistingPatientUrl(this.props.profileUrl);

            return (
                <styled.MobileExistingPatientLink
                    data-test="ap-mobile-content-existing-patient-link"
                    href={urlWithExistingPatientFlag}
                >
                    <styled.MobileExistingPatientLinkText>
                        <FormattedMessage
                            defaultMessage={defaultMessages.ViewExistingPatientAvailability}
                            id="AppointmentPicker.DesktopView.Content.ViewExistingPatientAvailability"
                        />
                    </styled.MobileExistingPatientLinkText>
                    <styled.MobileStyledArrow
                        color="currentcolor"
                        direction="right"
                        height={10}
                    />
                </styled.MobileExistingPatientLink>
            );
        }
    }

    handleNextAvailClick = e => this.props.goToDayWithAvailBtnClicked(1, this.props.nextAvailDate, e);
    handleTimeClicked = e => this.props.onTimeClicked(false, e.currentTarget.getAttribute('href'), e);
}

/* eslint-disable babel/new-cap */
export default (
    CreateTimesGridContent(TIMESGRID_MOBILE_CONFIG)(
        MobileTimesGridContent
    )
);
